/**
 * P0-5 (DCTA-192, polish audit): a Bank card's header keeps its status pill in its row — app.css 67 lays it out as a grid of
 * the tag, what the card is, the total and the pill. What the card is (its count, "3 operations") wraps between its words
 * in the one flexible column. When a word of it is wider than that column — a pill beside a long total at 360 px, or in
 * large type — the count moves to a row of its own under the tag (`data-stack`), whole, instead of breaking inside a word
 * (measured: the column broke "operations" into one or two letters a line at 360 in large type).
 *
 * Measured, never guessed: the column is what the header's width leaves after the tag, the total and the pill and their
 * gaps; the word is measured in the count's own font. Neither depends on where the count sits, so a row of its own never
 * flips back and forth. Presentation only: nothing here reads or changes what the card says.
 */
const GAP = 8;

/** Whether the count needs a row of its own — measured, nothing written; undefined where there is nothing to fit. */
function stacks(bh: HTMLElement): boolean | undefined {
  const count = bh.querySelector<HTMLElement>(":scope > .count");
  if (count === null || !bh.isConnected) return undefined;
  const need = longestWord(count);
  if (need === undefined) return undefined;
  const cs = getComputedStyle(bh);
  let room = bh.getBoundingClientRect().width - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight) - GAP;
  for (const part of bh.querySelectorAll<HTMLElement>(":scope > .banktag, :scope > .money, :scope > .status")) {
    if (part.hidden) continue;
    const w = part.getBoundingClientRect().width;
    if (w > 0) room -= w + (part.classList.contains("banktag") ? 0 : GAP);
  }
  return need > room + 0.5;
}

/** Each header's verdict read first, then all written: one layout however many cards come back at a new width. */
function fitAll(heads: Iterable<HTMLElement>): void {
  const verdicts = [...heads].map((bh) => [bh, stacks(bh)] as const);
  for (const [bh, stack] of verdicts) {
    if (stack === true) bh.setAttribute("data-stack", "true");
    else if (stack === false) bh.removeAttribute("data-stack");
  }
}

/** Fit one header now (its pill's words changed). */
export function fitHeader(bh: HTMLElement): void {
  fitAll([bh]);
}

let observer: ResizeObserver | undefined;

/**
 * Fit the header whenever its size changes (the screen's width, the type size) — one observer for every card's header —
 * and every header on the page again when a font arrives (the words' widths change, not always the header's box).
 */
export function watchHeader(bh: HTMLElement): void {
  if (typeof ResizeObserver === "undefined") return;
  if (observer === undefined) {
    observer = new ResizeObserver((entries) => fitAll(entries.map((e) => e.target as HTMLElement)));
    document.fonts?.addEventListener?.("loadingdone", () => fitAll(document.querySelectorAll<HTMLElement>(".bank .bh")));
  }
  observer.observe(bh);
}

let ruler: CanvasRenderingContext2D | null | undefined;

/** The widest word of the element's text, in its own computed font; undefined where nothing can measure (no canvas). */
function longestWord(node: HTMLElement): number | undefined {
  ruler ??= document.createElement("canvas").getContext?.("2d") ?? null;
  if (ruler === null) return undefined;
  const cs = getComputedStyle(node);
  ruler.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
  const ctx = ruler;
  return Math.max(0, ...(node.textContent ?? "").split(/\s+/).map((w) => ctx.measureText(w).width));
}
